/* Stage 0 — Idea-to-Script Workbench
 *
 * Multi-model script ensemble + Opus 4.7 synthesis. Three frontier LLMs draft
 * in parallel from a structured brief; Opus then either picks the strongest
 * or synthesizes a new canonical taking the best from each.
 *
 * UX phases:
 *   form     → user fills brief, clicks Generate Scripts
 *   running  → 3 per-model progress tiles + Opus synthesis tile
 *   complete → canonical card on top, 3 collapsible draft tabs below,
 *              3 exit actions at the bottom
 *   failed   → error banner + Retry button
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../utils/toast';
import {
  ArrowLeft, Sparkles, RotateCcw, FileText, Loader2, X, ChevronDown,
  Check, AlertTriangle, Copy, Wand2,
} from 'lucide-react';
import ActionButton from '../components/ActionButton';

const API = window.__API_BASE + '/api';
const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

// ─── constants ─────────────────────────────────────────────────────────────
const TONE_OPTIONS = [
  'Premium', 'Playful', 'Emotional', 'Edgy', 'Restrained',
  'Cinematic', 'Documentary', 'Comedic', 'Sentimental', 'Aspirational',
];
const GENRE_OPTIONS = [
  'Drama', 'Comedy', 'Action', 'Documentary', 'Lifestyle', 'Testimonial',
  'Parody', 'Slice-of-life', 'Anthem', 'Story-led', 'Product-led',
];
const LANGUAGE_OPTIONS = [
  'Hindi', 'English', 'Hinglish', 'Tamil', 'Telugu', 'Bengali', 'Marathi',
];
const PLATFORM_OPTIONS = [
  'TV', 'OTT', 'YouTube', 'Instagram Reels', 'Meta Feed', 'Stories', 'Cinema',
];
const CAST_REGISTER_OPTIONS = [
  'Gen-Z', 'Millennial', 'Family', 'Premium', 'Athletic', 'Corporate',
  'Rural', 'Urban',
];

const SOURCE_BADGE = {
  synthesized: { label: 'Synthesized from all 3', color: 'bg-ai-accent/15 text-ai-accent border-ai-accent/30' },
  picked_opus: { label: "Picked Director AI's draft as strongest", color: 'bg-ai-accent/15 text-ai-accent border-ai-accent/30' },
  picked_gpt:  { label: "Picked Writer AI's draft as strongest", color: 'bg-ai-accent/15 text-ai-accent border-ai-accent/30' },
  picked_gemini: { label: "Picked Sketch AI's draft as strongest", color: 'bg-ai-accent/15 text-ai-accent border-ai-accent/30' },
  user_provided: { label: 'User-provided', color: 'bg-white/5 text-white/70 border-white/15' },
};


// ─── helpers ───────────────────────────────────────────────────────────────
function MultiSelectChips({ options, value, onChange, testId }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {options.map((opt) => {
        const active = value.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(active ? value.filter((v) => v !== opt) : [...value, opt])}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all active:scale-95 ${
              active
                ? 'bg-ai-accent text-white border-ai-accent'
                : 'bg-white/5 text-white/70 border-white/15 hover:bg-white/10'
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}


function ModelTile({ tile }) {
  // Visual states: pending (grey), running (blue spinner), ok (green-tinted),
  // error (red border). Status text shows elapsed time + a friendly line.
  const isRunning = tile.status === 'running';
  const isOk = tile.status === 'ok';
  const isError = tile.status === 'error' || tile.status === 'cancelled';
  return (
    <div
      data-testid={`model-progress-${tile.slug}`}
      className={`p-3 rounded-lg border flex items-center gap-3 ${
        isOk ? 'border-ai-accent/30 bg-ai-accent/5'
        : isError ? 'border-red-500/30 bg-red-500/5'
        : 'border-white/10 bg-white/[0.02]'
      }`}
    >
      <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0">
        {isRunning && <Loader2 className="w-4 h-4 text-ai-accent animate-spin" />}
        {isOk && <Check className="w-4 h-4 text-ai-accent" />}
        {isError && <AlertTriangle className="w-4 h-4 text-red-400" />}
        {tile.status === 'pending' && <div className="w-2 h-2 rounded-full bg-white/30" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-white truncate">{tile.label}</div>
        <div className="text-[11px] text-white/60 truncate">
          {isError
            ? <span className="text-red-400">{tile.status_text || tile.status}</span>
            : <>{Number(tile.elapsed_s || 0).toFixed(0)}s · {tile.status_text || tile.status}</>}
        </div>
      </div>
    </div>
  );
}


function DraftTab({ draft, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/[0.03] transition-all"
        data-testid={`${draft.slug}-draft-tab`}
      >
        <div className="flex items-center gap-2">
          <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${open ? '' : '-rotate-90'}`} />
          <span className="text-sm font-medium text-white">{draft.label}</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-white/5 text-white/50">{draft.model}</span>
          {draft.status === 'ok' && (
            <span className="text-[10px] text-white/40">
              {draft.tokens_in || 0}+{draft.tokens_out || 0} tok · ${Number(draft.cost_usd || 0).toFixed(3)}
            </span>
          )}
          {draft.status === 'error' && (
            <span className="text-[10px] text-red-400">failed</span>
          )}
        </div>
      </button>
      {open && (
        <div className="px-4 py-3 border-t border-white/5">
          {draft.status === 'ok' ? (
            <pre className="text-[11px] text-white/75 whitespace-pre-wrap font-mono leading-relaxed max-h-[400px] overflow-y-auto">
              {draft.content}
            </pre>
          ) : (
            <p className="text-xs text-red-400/80">
              {draft.error || 'Draft unavailable.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}


// ─── main view ─────────────────────────────────────────────────────────────
export default function IdeaToScriptWorkbench() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [moreOpen, setMoreOpen] = useState(false);

  // Brief state — mirrors backend payload schema.
  const [brief, setBrief] = useState({
    idea: '',
    brand_name: '',
    product_name: '',
    duration: 30,
    aspect_ratio: '16:9',
    languages: ['Hindi', 'English'],
    tone: [],
    genre: '',
    customer_tg: '',
    age_range: { min: 18, max: 45 },
    campaign_objective: '',
    platforms: [],
    hard_nos: '',
    reference_films: ['', '', ''],
    music_style: '',
    cast_register: [],
    cast_count_cap: '',
    location_count_cap: '',
    era_setting: 'Contemporary',
    competitor_films: ['', '', ''],
    call_to_action: '',
  });

  // Run state
  const [phase, setPhase] = useState('form'); // form | running | complete | failed
  const [statusDoc, setStatusDoc] = useState(null);
  const [showRegenConfirm, setShowRegenConfirm] = useState(false);
  const pollRef = useRef(null);
  const ranOnceRef = useRef(false);

  // ── load project once ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/projects/${projectId}`, { headers: getHeaders() });
        if (cancelled) return;
        setProject(r.data);
        // Hydrate brief from any saved one + pull aspect/duration from the project.
        setBrief((b) => ({
          ...b,
          aspect_ratio: r.data?.orientation || b.aspect_ratio,
          duration: r.data?.duration || b.duration,
          brand_name: r.data?.idea_brief?.brand_name || b.brand_name,
          product_name: r.data?.idea_brief?.product_name || b.product_name,
          idea: r.data?.idea_brief?.idea || b.idea,
          ...(r.data?.idea_brief || {}),
        }));
        // If a run is already in flight or completed, surface it.
        const st = r.data?.idea_to_script_status;
        if (st?.phase && st.phase !== 'idle') {
          // The /status endpoint hydrates drafts+canonical for completed
          // runs; the raw project doc only has the bare status. So always
          // hit /status when there's an active or complete run.
          try {
            const sres = await axios.get(
              `${API}/projects/${projectId}/idea-to-script/status`,
              { headers: getHeaders() }
            );
            setStatusDoc(sres.data);
            if (sres.data.complete) {
              setPhase(sres.data.phase === 'failed' ? 'failed' : 'complete');
            } else {
              setPhase('running');
            }
          } catch (_) {
            setStatusDoc(st);
            if (st.complete) setPhase(st.phase === 'failed' ? 'failed' : 'complete');
            else setPhase('running');
          }
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load project');
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  // ── polling ──
  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    const tick = async () => {
      try {
        const r = await axios.get(
          `${API}/projects/${projectId}/idea-to-script/status`,
          { headers: getHeaders() }
        );
        setStatusDoc(r.data);
        if (r.data.complete) {
          if (r.data.phase === 'complete') setPhase('complete');
          else if (r.data.phase === 'failed') setPhase('failed');
          else if (r.data.phase === 'cancelled') setPhase('form');
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        }
      } catch (e) {
        // soft-fail; keep polling
      }
    };
    tick();
    pollRef.current = setInterval(tick, 3500);
  }, [projectId]);

  useEffect(() => {
    if (phase === 'running' && !pollRef.current) startPolling();
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [phase, startPolling]);

  // If the page mounts and a run is already running, kick off polling.
  useEffect(() => {
    if (statusDoc && !statusDoc.complete && phase === 'running' && !pollRef.current && !ranOnceRef.current) {
      ranOnceRef.current = true;
      startPolling();
    }
  }, [statusDoc, phase, startPolling]);

  // ── validation ──
  const briefValid = useMemo(() => {
    return (
      (brief.idea || '').trim().length >= 20
      && (brief.brand_name || '').trim().length > 0
      && (brief.product_name || '').trim().length > 0
      && Number(brief.duration) >= 5
      && (brief.tone || []).length > 0
    );
  }, [brief]);

  const validationHint = useMemo(() => {
    if ((brief.idea || '').trim().length < 20) return 'Idea must be at least 20 characters.';
    if (!(brief.brand_name || '').trim()) return 'Brand name is required.';
    if (!(brief.product_name || '').trim()) return 'Product name is required.';
    if ((brief.tone || []).length === 0) return 'Pick at least one tone chip.';
    return '';
  }, [brief]);

  // ── actions ──
  const fireGenerate = useCallback(async () => {
    if (!briefValid) {
      toast.warning(validationHint || 'Fill required fields first.');
      return;
    }
    const payload = {
      ...brief,
      reference_films: (brief.reference_films || []).filter((u) => u && u.trim()),
      competitor_films: (brief.competitor_films || []).filter((u) => u && u.trim()),
    };
    setPhase('running');
    setStatusDoc({ phase: 'generating', complete: false, models: [
      { slug: 'gpt', label: 'Writer AI', status: 'pending', elapsed_s: 0 },
      { slug: 'opus', label: 'Director AI', status: 'pending', elapsed_s: 0 },
      { slug: 'gemini', label: 'Sketch AI', status: 'pending', elapsed_s: 0 },
    ]});
    try {
      await axios.post(
        `${API}/projects/${projectId}/idea-to-script/generate`,
        payload,
        { headers: getHeaders() }
      );
      startPolling();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start generation');
      setPhase('form');
    }
  }, [brief, briefValid, validationHint, projectId, startPolling]);

  const cancelGeneration = useCallback(async () => {
    try {
      await axios.post(
        `${API}/projects/${projectId}/idea-to-script/cancel`,
        { run_id: statusDoc?.run_id },
        { headers: getHeaders() }
      );
      toast.info('Cancelling generation…');
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      setTimeout(() => setPhase('form'), 600);
    } catch (e) {
      toast.error('Cancel failed: ' + (e.response?.data?.detail || e.message));
    }
  }, [projectId, statusDoc]);

  const regenerate = useCallback(() => {
    const count = Number(statusDoc?.regenerate_count || 0);
    if (count >= 5) {
      setShowRegenConfirm(true);
      return;
    }
    fireGenerate();
  }, [statusDoc, fireGenerate]);

  const confirmRegen = useCallback(() => {
    setShowRegenConfirm(false);
    fireGenerate();
  }, [fireGenerate]);

  const acceptGenerated = useCallback(async () => {
    try {
      const r = await axios.post(
        `${API}/projects/${projectId}/idea-to-script/accept`,
        { action: 'use_generated' },
        { headers: getHeaders() }
      );
      toast.success('Script accepted. Opening Stage 1…');
      navigate(r.data.redirect || `/project/${projectId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Accept failed');
    }
  }, [projectId, navigate]);

  const acceptOwn = useCallback(async () => {
    try {
      const r = await axios.post(
        `${API}/projects/${projectId}/idea-to-script/accept`,
        { action: 'user_provided' },
        { headers: getHeaders() }
      );
      toast.info('Drafts archived. Opening Stage 1 with a blank script…');
      navigate(r.data.redirect || `/project/${projectId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to proceed');
    }
  }, [projectId, navigate]);

  // ── output panel state ──
  const canonical = useMemo(() => {
    if (!statusDoc || phase !== 'complete') return null;
    return statusDoc.canonical || null;
  }, [statusDoc, phase]);

  const drafts = useMemo(() => statusDoc?.drafts || [], [statusDoc]);
  const canonicalSource = canonical?.source;
  const badge = canonicalSource ? SOURCE_BADGE[canonicalSource] : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-ai-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-ai-accent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ai-bg text-white" data-testid="stage-0-workbench">
      {/* Header */}
      <header className="border-b border-white/10 bg-ai-bg/95 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate('/')}
              className="p-1.5 rounded-md hover:bg-white/5 transition-all"
              title="Back to Dashboard"
              data-testid="workbench-back"
            >
              <ArrowLeft className="w-4 h-4 text-white/70" />
            </button>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-ai-accent font-semibold">
                Stage 0/17 · Idea to Script
              </div>
              <div className="text-sm font-medium text-white truncate">
                {project?.name || 'Untitled project'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="hidden sm:inline text-[11px] text-white/40">
              3 frontier models · AI synthesis
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── LEFT — Brief Form ─────────────────────────────────────── */}
        <section data-testid="idea-brief-form" className="space-y-5">
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 space-y-4">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-ai-accent" />
              Idea Brief
            </h2>
            {/* Idea */}
            <div>
              <label className="text-[11px] font-medium text-white/70 mb-1 block">
                Idea <span className="text-red-400">*</span>
              </label>
              <textarea
                value={brief.idea}
                onChange={(e) => setBrief({ ...brief, idea: e.target.value })}
                rows={4}
                placeholder="Describe the spot you imagine. Free-form. The more vivid the idea, the sharper the drafts."
                maxLength={3000}
                className="w-full rounded-lg bg-ai-bg border border-white/10 px-3 py-2 text-sm text-white placeholder-white/30 focus:border-ai-accent focus:outline-none resize-none"
                data-testid="idea-input"
              />
              <div className="text-[10px] text-white/40 text-right mt-1">{(brief.idea || '').length}/3000</div>
            </div>

            {/* Brand + Product */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-medium text-white/70 mb-1 block">Brand <span className="text-red-400">*</span></label>
                <input
                  value={brief.brand_name}
                  onChange={(e) => setBrief({ ...brief, brand_name: e.target.value })}
                  placeholder="e.g., Hero MotoCorp"
                  className="w-full rounded-lg bg-ai-bg border border-white/10 px-3 py-2 text-sm text-white placeholder-white/30 focus:border-ai-accent focus:outline-none"
                  data-testid="brand-name-input"
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-white/70 mb-1 block">Product / SKU <span className="text-red-400">*</span></label>
                <input
                  value={brief.product_name}
                  onChange={(e) => setBrief({ ...brief, product_name: e.target.value })}
                  placeholder="e.g., Splendor Plus"
                  className="w-full rounded-lg bg-ai-bg border border-white/10 px-3 py-2 text-sm text-white placeholder-white/30 focus:border-ai-accent focus:outline-none"
                  data-testid="product-name-input"
                />
              </div>
            </div>

            {/* Duration + Aspect */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-medium text-white/70 mb-1 block">Duration (sec)</label>
                <input
                  type="number"
                  min={10} max={180}
                  value={brief.duration}
                  onChange={(e) => setBrief({ ...brief, duration: Math.max(10, Math.min(180, Number(e.target.value) || 30)) })}
                  className="w-full rounded-lg bg-ai-bg border border-white/10 px-3 py-2 text-sm text-white focus:border-ai-accent focus:outline-none"
                  data-testid="duration-input"
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-white/70 mb-1 block">Aspect ratio</label>
                <div className="flex gap-1.5">
                  {['16:9', '9:16', '1:1'].map((ar) => (
                    <button
                      key={ar}
                      type="button"
                      onClick={() => setBrief({ ...brief, aspect_ratio: ar })}
                      className={`flex-1 py-1.5 rounded-md text-xs font-medium border transition-all ${
                        brief.aspect_ratio === ar
                          ? 'bg-ai-accent text-white border-ai-accent'
                          : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'
                      }`}
                      data-testid={`aspect-${ar.replace(':', '-')}`}
                    >{ar}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* Languages */}
            <div>
              <label className="text-[11px] font-medium text-white/70 mb-1 block">Language(s)</label>
              <MultiSelectChips
                options={LANGUAGE_OPTIONS}
                value={brief.languages}
                onChange={(v) => setBrief({ ...brief, languages: v })}
                testId="languages-chips"
              />
            </div>

            {/* Tone */}
            <div>
              <label className="text-[11px] font-medium text-white/70 mb-1 block">Tone <span className="text-red-400">*</span></label>
              <MultiSelectChips
                options={TONE_OPTIONS}
                value={brief.tone}
                onChange={(v) => setBrief({ ...brief, tone: v })}
                testId="tone-chips"
              />
            </div>

            {/* Genre */}
            <div>
              <label className="text-[11px] font-medium text-white/70 mb-1 block">Genre</label>
              <select
                value={brief.genre}
                onChange={(e) => setBrief({ ...brief, genre: e.target.value })}
                className="w-full rounded-lg bg-ai-bg border border-white/10 px-3 py-2 text-sm text-white focus:border-ai-accent focus:outline-none"
                data-testid="genre-select"
              >
                <option value="">— Pick one —</option>
                {GENRE_OPTIONS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>

            {/* Customer TG */}
            <div>
              <label className="text-[11px] font-medium text-white/70 mb-1 block">Customer TG</label>
              <input
                value={brief.customer_tg}
                onChange={(e) => setBrief({ ...brief, customer_tg: e.target.value })}
                placeholder="e.g., Tier-2/3 first-time bike buyers"
                className="w-full rounded-lg bg-ai-bg border border-white/10 px-3 py-2 text-sm text-white placeholder-white/30 focus:border-ai-accent focus:outline-none"
                data-testid="customer-tg-input"
              />
              <div className="mt-2 flex items-center gap-3">
                <span className="text-[10px] text-white/50">Age:</span>
                <input
                  type="number"
                  min={15} max={64}
                  value={brief.age_range.min}
                  onChange={(e) => setBrief({ ...brief, age_range: { ...brief.age_range, min: Number(e.target.value) || 18 } })}
                  className="w-16 rounded-md bg-ai-bg border border-white/10 px-2 py-1 text-[11px] text-white"
                  data-testid="age-min-input"
                />
                <span className="text-[10px] text-white/40">to</span>
                <input
                  type="number"
                  min={16} max={65}
                  value={brief.age_range.max}
                  onChange={(e) => setBrief({ ...brief, age_range: { ...brief.age_range, max: Number(e.target.value) || 45 } })}
                  className="w-16 rounded-md bg-ai-bg border border-white/10 px-2 py-1 text-[11px] text-white"
                  data-testid="age-max-input"
                />
              </div>
            </div>
          </div>

          {/* More context accordion */}
          <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
            <button
              type="button"
              onClick={() => setMoreOpen((o) => !o)}
              className="w-full px-5 py-3 flex items-center justify-between hover:bg-white/[0.03] transition-all"
              data-testid="more-context-toggle"
            >
              <span className="text-sm font-semibold text-white">More context (optional)</span>
              <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${moreOpen ? '' : '-rotate-90'}`} />
            </button>
            {moreOpen && (
              <div className="px-5 py-4 border-t border-white/5 space-y-4">
                {/* Campaign objective */}
                <div>
                  <label className="text-[11px] font-medium text-white/70 mb-1 block">Campaign objective</label>
                  <select
                    value={brief.campaign_objective}
                    onChange={(e) => setBrief({ ...brief, campaign_objective: e.target.value })}
                    className="w-full rounded-lg bg-ai-bg border border-white/10 px-3 py-2 text-sm text-white focus:border-ai-accent focus:outline-none"
                    data-testid="campaign-objective-select"
                  >
                    <option value="">— pick —</option>
                    {['Awareness', 'Consideration', 'Conversion', 'Engagement', 'Retention'].map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-medium text-white/70 mb-1 block">Platforms</label>
                  <MultiSelectChips options={PLATFORM_OPTIONS} value={brief.platforms} onChange={(v) => setBrief({ ...brief, platforms: v })} testId="platforms-chips" />
                </div>
                <div>
                  <label className="text-[11px] font-medium text-white/70 mb-1 block">Hard nos / anti-patterns</label>
                  <textarea
                    rows={2}
                    value={brief.hard_nos}
                    onChange={(e) => setBrief({ ...brief, hard_nos: e.target.value })}
                    placeholder="Never show / never reference / avoid…"
                    className="w-full rounded-lg bg-ai-bg border border-white/10 px-3 py-2 text-sm text-white placeholder-white/30 focus:border-ai-accent focus:outline-none resize-none"
                    data-testid="hard-nos-input"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-medium text-white/70 mb-1 block">Reference films (up to 3 URLs)</label>
                  {[0, 1, 2].map((i) => (
                    <input
                      key={i}
                      value={brief.reference_films[i] || ''}
                      onChange={(e) => {
                        const arr = [...brief.reference_films];
                        arr[i] = e.target.value;
                        setBrief({ ...brief, reference_films: arr });
                      }}
                      placeholder={`https://… (#${i + 1})`}
                      className="w-full mb-1.5 rounded-lg bg-ai-bg border border-white/10 px-3 py-1.5 text-xs text-white placeholder-white/30 focus:border-ai-accent focus:outline-none"
                      data-testid={`ref-film-${i}`}
                    />
                  ))}
                </div>
                <div>
                  <label className="text-[11px] font-medium text-white/70 mb-1 block">Music style preference</label>
                  <select
                    value={brief.music_style}
                    onChange={(e) => setBrief({ ...brief, music_style: e.target.value })}
                    className="w-full rounded-lg bg-ai-bg border border-white/10 px-3 py-2 text-sm text-white focus:border-ai-accent focus:outline-none"
                    data-testid="music-style-select"
                  >
                    <option value="">— pick —</option>
                    {['Cinematic', 'Hip-hop', 'Classical', 'Folk', 'Ambient', 'Silence'].map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-medium text-white/70 mb-1 block">Cast register</label>
                  <MultiSelectChips options={CAST_REGISTER_OPTIONS} value={brief.cast_register} onChange={(v) => setBrief({ ...brief, cast_register: v })} testId="cast-register-chips" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-medium text-white/70 mb-1 block">Cast count cap</label>
                    <div className="flex flex-wrap gap-1">
                      {['Single-hero', 'Duo', 'Trio', 'Ensemble'].map((c) => (
                        <button key={c} type="button"
                          onClick={() => setBrief({ ...brief, cast_count_cap: c })}
                          className={`px-2 py-1 rounded-md text-[10px] font-medium border ${brief.cast_count_cap === c ? 'bg-ai-accent text-white border-ai-accent' : 'bg-white/5 text-white/70 border-white/10'}`}
                        >{c}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-white/70 mb-1 block">Location count cap</label>
                    <div className="flex flex-wrap gap-1">
                      {['Single-set', 'Two-location', 'Multi-location'].map((c) => (
                        <button key={c} type="button"
                          onClick={() => setBrief({ ...brief, location_count_cap: c })}
                          className={`px-2 py-1 rounded-md text-[10px] font-medium border ${brief.location_count_cap === c ? 'bg-ai-accent text-white border-ai-accent' : 'bg-white/5 text-white/70 border-white/10'}`}
                        >{c}</button>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-medium text-white/70 mb-1 block">Era / setting</label>
                  <div className="flex gap-1.5">
                    {['Contemporary', 'Period', 'Futuristic', 'Surreal'].map((e) => (
                      <button key={e} type="button"
                        onClick={() => setBrief({ ...brief, era_setting: e })}
                        className={`px-2 py-1 rounded-md text-[10px] font-medium border ${brief.era_setting === e ? 'bg-ai-accent text-white border-ai-accent' : 'bg-white/5 text-white/70 border-white/10'}`}
                      >{e}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-medium text-white/70 mb-1 block">Competitor films (differentiate from)</label>
                  {[0, 1, 2].map((i) => (
                    <input
                      key={i}
                      value={brief.competitor_films[i] || ''}
                      onChange={(e) => {
                        const arr = [...brief.competitor_films];
                        arr[i] = e.target.value;
                        setBrief({ ...brief, competitor_films: arr });
                      }}
                      placeholder={`https://… (#${i + 1})`}
                      className="w-full mb-1.5 rounded-lg bg-ai-bg border border-white/10 px-3 py-1.5 text-xs text-white placeholder-white/30 focus:border-ai-accent focus:outline-none"
                      data-testid={`competitor-film-${i}`}
                    />
                  ))}
                </div>
                <div>
                  <label className="text-[11px] font-medium text-white/70 mb-1 block">Call to action</label>
                  <input
                    value={brief.call_to_action}
                    onChange={(e) => setBrief({ ...brief, call_to_action: e.target.value })}
                    placeholder="What the spot asks the viewer to do."
                    className="w-full rounded-lg bg-ai-bg border border-white/10 px-3 py-2 text-sm text-white placeholder-white/30 focus:border-ai-accent focus:outline-none"
                    data-testid="cta-input"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer CTA */}
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/40">Estimated cost</div>
              <div className="text-sm font-bold text-ai-accent" data-testid="cost-pill-estimated">
                ~₹50 · $0.60
              </div>
              <div className="text-[10px] text-white/40 mt-0.5">3 AI drafts + Director AI synthesis</div>
            </div>
            <ActionButton
              variant="primary"
              icon={Sparkles}
              onClick={fireGenerate}
              disabled={!briefValid || phase === 'running'}
              loading={phase === 'running'}
              title={!briefValid ? validationHint : 'Fire 3 LLM drafts in parallel'}
              data-testid="generate-scripts-cta"
              className="text-sm"
            >
              {phase === 'running' ? 'Generating…' : 'Generate Scripts'}
            </ActionButton>
          </div>
          {!briefValid && (
            <p className="text-[11px] text-amber-400/80 px-1" data-testid="validation-hint">
              {validationHint}
            </p>
          )}
        </section>

        {/* ── RIGHT — Output panel ────────────────────────────────── */}
        <section className="space-y-4">
          {/* Idle state */}
          {phase === 'form' && (
            <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-8 text-center">
              <FileText className="w-10 h-10 text-white/20 mx-auto mb-3" />
              <h3 className="text-sm font-semibold text-white/70">Fill the brief and click Generate</h3>
              <p className="text-xs text-white/40 mt-2 max-w-md mx-auto leading-relaxed">
                Three frontier AI models will draft your script in parallel.
                Then AI will pick the strongest or synthesize a new canonical from
                the best of each. Takes ~2-4 minutes.
              </p>
            </div>
          )}

          {/* Running / Synthesizing */}
          {(phase === 'running') && statusDoc && (
            <div className="space-y-3">
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-ai-accent font-semibold">
                    {statusDoc.phase === 'synthesizing' ? 'Phase 2 · AI synthesis' : 'Phase 1 · Drafting'}
                  </div>
                  <div className="text-sm font-medium text-white mt-0.5">
                    {statusDoc.phase === 'synthesizing'
                      ? 'AI is reviewing the three drafts…'
                      : 'Three models drafting in parallel'}
                  </div>
                </div>
                <button
                  onClick={cancelGeneration}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all active:scale-95"
                  data-testid="cancel-generation"
                >
                  <X className="w-3.5 h-3.5 inline -mt-px mr-1" />
                  Cancel
                </button>
              </div>

              {(statusDoc.models || []).map((m) => <ModelTile key={m.slug} tile={m} />)}

              {statusDoc.phase === 'synthesizing' && (
                <div
                  data-testid="synthesis-progress"
                  className="p-3 rounded-lg border border-ai-accent/30 bg-ai-accent/5 flex items-center gap-3"
                >
                  <Loader2 className="w-4 h-4 text-ai-accent animate-spin" />
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-white">Director AI finalizing</div>
                    <div className="text-[11px] text-white/60">Synthesizing or picking the strongest draft…</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Failed */}
          {phase === 'failed' && statusDoc && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-5">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <h3 className="text-sm font-semibold text-red-400">Generation failed</h3>
              </div>
              <p className="text-xs text-white/70 mb-4">{statusDoc.error || 'Unknown error.'}</p>
              <ActionButton variant="primary" icon={RotateCcw} onClick={() => setPhase('form')} data-testid="retry-from-failed">
                Try again
              </ActionButton>
            </div>
          )}

          {/* Complete */}
          {phase === 'complete' && canonical && (
            <>
              <div data-testid="canonical-script-card" className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
                <div className="px-5 py-4 border-b border-white/5 flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    {badge && (
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium border ${badge.color}`}>
                        <Check className="w-2.5 h-2.5 inline -mt-px mr-1" />
                        {badge.label}
                      </span>
                    )}
                    <h3 className="text-base font-bold text-white mt-2">Canonical script</h3>
                    {canonical.reasoning && (
                      <p className="text-[12px] text-white/60 mt-1.5 italic leading-relaxed" data-testid="canonical-reasoning-text">
                        {canonical.reasoning}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(canonical.content || '');
                      toast.success('Canonical copied');
                    }}
                    className="p-1.5 rounded-md hover:bg-white/5 transition-all"
                    title="Copy canonical script"
                    data-testid="copy-canonical"
                  >
                    <Copy className="w-3.5 h-3.5 text-white/60" />
                  </button>
                </div>
                <pre className="px-5 py-4 text-[12px] text-white/80 whitespace-pre-wrap font-mono leading-relaxed max-h-[480px] overflow-y-auto" data-testid="canonical-script-body">
                  {canonical.content}
                </pre>
              </div>

              {/* Individual drafts */}
              <div className="space-y-2">
                <h4 className="text-[10px] uppercase tracking-wider text-white/40 font-semibold mt-2">
                  Individual drafts ({drafts.filter(d => d.status === 'ok').length}/{drafts.length})
                </h4>
                {drafts.map((d) => <DraftTab key={d.slug} draft={d} />)}
              </div>

              {/* Exit actions */}
              <div className="flex flex-col sm:flex-row gap-2 pt-3">
                <button
                  onClick={acceptOwn}
                  className="flex-1 px-3 py-2.5 rounded-lg text-xs font-medium border border-white/15 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-all active:scale-95"
                  data-testid="use-my-own-script"
                >
                  I have my own script
                </button>
                <button
                  onClick={regenerate}
                  className="flex-1 px-3 py-2.5 rounded-lg text-xs font-medium border border-ai-accent/30 bg-ai-accent/10 text-ai-accent hover:bg-ai-accent/20 transition-all active:scale-95 flex items-center justify-center gap-1.5"
                  data-testid="regenerate-scripts"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Regenerate
                </button>
                <ActionButton
                  variant="primary"
                  onClick={acceptGenerated}
                  data-testid="use-generated-script"
                  className="flex-1 justify-center"
                >
                  Use generated script →
                </ActionButton>
              </div>
            </>
          )}
        </section>
      </main>

      {/* Regen confirm modal */}
      {showRegenConfirm && (
        <div
          data-testid="regenerate-confirm-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setShowRegenConfirm(false)}
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-ai-bg border border-white/10 rounded-xl p-6 max-w-md w-full">
            <h3 className="text-base font-bold text-white mb-2">Regenerate again?</h3>
            <p className="text-xs text-white/70 mb-4 leading-relaxed">
              You've regenerated {statusDoc?.regenerate_count || 0} times. Each regen
              costs ~₹50 ($0.60) and takes ~2-4 minutes. Continue?
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowRegenConfirm(false)}
                className="px-3 py-2 rounded-lg text-xs font-medium border border-white/15 bg-white/5 text-white/70 hover:bg-white/10"
                data-testid="regen-confirm-cancel"
              >Cancel</button>
              <ActionButton variant="primary" onClick={confirmRegen} data-testid="regen-confirm-continue">
                Continue anyway
              </ActionButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
